import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { correctAnswerIssues, duplicateValueIssues, feedbackIssues, missingValueIssues, optionIssues, requireOptions, trimmed, valuesOf } from './_checks.js';
import { draftFields, Option, questionFields } from './_shared.js';
import { activityIssue, asList } from './types.js';

const issue = activityIssue('drag_ordering');

/**
 * Drag the items into an order. `correctAnswerOrder` is a list of option *values*, and
 * it has to be a permutation of them — an order naming an item that is not there, or
 * leaving one out, cannot be reached by dragging, so the activity is unpassable. An
 * absent order is fine: the activity then plays as a story beat rather than a test.
 */
export const dragOrdering: ActivityDefinition = {
  type: 'drag_ordering',
  summary: 'Drag items into the right order.',
  setup: z.looseObject({
    ...draftFields,
    ...questionFields,
    options: z.array(Option).optional(),
    correctAnswerOrder: z.array(z.string()).optional(),
  }),
  check: (setup, ctx) => {
    const options = asList(setup.options);
    const issues = [
      ...requireOptions('drag_ordering', options, ctx),
      ...optionIssues('drag_ordering', options, ctx),
      ...duplicateValueIssues('drag_ordering', options, ctx),
      ...missingValueIssues('drag_ordering', options, ctx),
      ...feedbackIssues('drag_ordering', setup.feedback, ctx),
    ];

    const order = asList(setup.correctAnswerOrder).map(trimmed).filter(Boolean);
    if (!order.length) {
      return issues;
    }

    const values = valuesOf(options);
    const orderPath = [...ctx.path, 'setup', 'correctAnswerOrder'];

    issues.push(...correctAnswerIssues({
      type: 'drag_ordering',
      field: 'correctAnswerOrder',
      answers: order,
      values,
      ctx,
      indexed: true,
    }));

    const missing = values.filter(value => !order.includes(value));
    if (missing.length) {
      issues.push(issue(
        'incompleteOrder',
        `correctAnswerOrder leaves out ${missing.join(', ')} — every item has to have a place in the order.`,
        orderPath,
        ctx,
        order,
      ));
    }

    return issues;
  },
};
